import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

interface SlideItem {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  coverImage: string;
  link: string;
}

const heroSlides: SlideItem[] = [
  {
    id: '1',
    title: '仙尊归来',
    subtitle: '全网热销第一 · 9.7分神作',
    description: '修真界第一仙尊重生少年时代，带着万载记忆重新修炼，逆天崛起碾压一切强敌',
    coverImage: 'https://readdy.ai/api/search-image?query=Epic%20Korean%20manhwa%20style%20fantasy%20landscape%2C%20immortal%20cultivator%20silhouette%20on%20mountain%20peak%2C%20golden%20sunrise%20light%2C%20traditional%20eastern%20architecture%2C%20misty%20atmosphere%2C%20artistic%20illustration%2C%20warm%20amber%20and%20cream%20tones%2C%20dramatic%20composition%2C%20painterly%20style&width=1600&height=900&seq=hero-slide-01&orientation=landscape',
    link: '/comic/6',
  },
  {
    id: '2',
    title: '重生之豪门千金',
    subtitle: '热门追更 · 1.87亿阅读',
    description: '被陷害致死的豪门千金重生十年前，带着记忆步步为营揭开惊天阴谋',
    coverImage: 'https://readdy.ai/api/search-image?query=Elegant%20Korean%20manhwa%20style%20illustration%2C%20luxurious%20palace%20interior%20with%20chandeliers%2C%20silhouette%20of%20elegant%20woman%2C%20warm%20golden%20and%20beige%20tones%2C%20soft%20atmospheric%20lighting%2C%20artistic%20painterly%20style%2C%20dramatic%20and%20romantic%20mood&width=1600&height=900&seq=hero-slide-02&orientation=landscape',
    link: '/comic/4',
  },
  {
    id: '3',
    title: '猎魔人传说',
    subtitle: '热血推荐 · 9.4分',
    description: '拥有特殊能力的猎魔人，在妖魔横行的都市中守护人类，揭开身世之谜',
    coverImage: 'https://readdy.ai/api/search-image?query=Dark%20urban%20fantasy%20Korean%20manhwa%20style%20illustration%2C%20mysterious%20hunter%20silhouette%20in%20city%20night%2C%20neon%20reflections%2C%20dramatic%20lighting%2C%20artistic%20painterly%20style%2C%20warm%20amber%20accents%2C%20atmospheric%20mood&width=1600&height=900&seq=hero-slide-03&orientation=landscape',
    link: '/comic/2',
  },
];

export default function HeroSection() {
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  const slide = heroSlides[currentSlide];

  return (
    <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
      {heroSlides.map((s, i) => (
        <div
          key={s.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === currentSlide ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={s.coverImage}
            alt={s.title}
            className="w-full h-full object-cover object-top"
            loading={i === 0 ? 'eager' : 'lazy'}
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/40"></div>

      <div className="absolute inset-0 flex items-center">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-2xl">
            <p className="text-accent-400 text-sm md:text-base font-medium mb-3 tracking-wide">
              {slide.subtitle}
            </p>
            <h2 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight">
              {slide.title}
            </h2>
            <p className="text-white/80 text-sm md:text-base leading-relaxed mb-6 max-w-md">
              {slide.description}
            </p>
            <Link
              to={slide.link}
              className="inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-600 text-background-50 px-6 py-3 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              立即阅读
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {heroSlides.map((_, i) => (
          <button
            key={i}
            onClick={() => goToSlide(i)}
            className={`w-2 h-2 rounded-full transition-all duration-300 cursor-pointer ${
              i === currentSlide
                ? 'bg-accent-500 w-6'
                : 'bg-white/50 hover:bg-white/80'
            }`}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </div>

      <button
        onClick={() => goToSlide((currentSlide - 1 + heroSlides.length) % heroSlides.length)}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 rounded-full bg-white/20 hover:bg-white/40 flex items-center justify-center text-white transition-all duration-200 cursor-pointer"
        aria-label="Previous slide"
      >
        <i className="ri-arrow-left-s-line text-lg"></i>
      </button>
      <button
        onClick={() => goToSlide((currentSlide + 1) % heroSlides.length)}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 rounded-full bg-white/20 hover:bg-white/40 flex items-center justify-center text-white transition-all duration-200 cursor-pointer"
        aria-label="Next slide"
      >
        <i className="ri-arrow-right-s-line text-lg"></i>
      </button>
    </section>
  );
}